// hooks/useBacnetUniversalControls.ts

import { useState, useEffect, useMemo, useCallback } from "react";
import { isEmpty, isEqual } from "lodash";
import { useGetBacnetParamTemplate } from "@hooks/useBacnetParamTemplatesApi";
import { useGetBacnetUiTemplate } from "@hooks/useBacnetUiTemplatesApi";
import { useSetBacnetObjectValue } from "@hooks/useBacnetDevicesApi";
import { useStoreActions, useStoreState } from "@models/RootStore";
import { bacnetUnits } from "@components/AddEditBacnetUiTemplateDialog/constants";
import { formatValue, getOperationalValue, getUniversalMappedPoints } from "../helpers";
import { withLatestOperationalData } from "@utils/latestOperationalData";

const EMPTY_OBJECT = {};

export function useBacnetUniversalControls(
  entry: any,
  siteUiTemplates?: Record<string, any>,
  siteParamTemplates?: Record<string, any>
) {
  const { id, operationalData: entryOperationalData = EMPTY_OBJECT } = entry;
  // Start from what arrived over the websocket since the entry was fetched, not just the entry itself
  const operationalData = useMemo(() => withLatestOperationalData(id, entryOperationalData), [id, entryOperationalData]);

  const types = useStoreState(s => s.types);
  const { temperatureScale = 0 } = useStoreState((s) => s.users.me);
  const { addMessage: addErrorMessage } = useStoreActions((a) => a.errorMessage);
  const { addWebSocketListener, removeWebSocketListener } = useStoreActions((a: any) => a);

  // ── Template loading ──────────────────────────────────────────────────────
  // When site-level template maps are provided (fetched once in the parent),
  // skip individual per-unit fetches entirely — all units get data at once.

  const uiTemplateId = typeof entry?.uiTemplate === 'string' ? entry?.uiTemplate : null;
  const uiTemplateQuery = useGetBacnetUiTemplate(siteUiTemplates || !uiTemplateId ? null : uiTemplateId) as any;
  const uiTemplate = typeof entry?.uiTemplate === 'object' && entry?.uiTemplate !== null
    ? entry.uiTemplate
    : siteUiTemplates && uiTemplateId
    ? siteUiTemplates[uiTemplateId]
    : uiTemplateQuery?.data;

  const entryParamTemplateId = typeof entry?.paramTemplate === 'string' ? entry?.paramTemplate : uiTemplate?.paramTemplate;
  const paramTemplateQuery = useGetBacnetParamTemplate(
    siteParamTemplates || !entryParamTemplateId || typeof entryParamTemplateId !== 'string' ? null : entryParamTemplateId
  ) as any;
  const paramTemplate = typeof entry?.paramTemplate === 'object' && entry?.paramTemplate !== null
    ? entry.paramTemplate
    : siteParamTemplates && entryParamTemplateId && siteParamTemplates[entryParamTemplateId]
    ? siteParamTemplates[entryParamTemplateId]
    : paramTemplateQuery?.data ||
      (entry?.paramTemplateData
        ? { entityTemplate: entry.paramTemplateData?.entityTemplate === true, data: entry.paramTemplateData }
        : undefined);

  const isLoadingTemplates = (siteUiTemplates && siteParamTemplates) || (entry?.paramTemplateData && !uiTemplateId)
    ? false
    : uiTemplateQuery?.isLoading || paramTemplateQuery?.isLoading;

  const { operationModesExtended } = types;

  // ── Live operational data via WebSocket ───────────────────────────────────

  const [dynamicOperationalData, setDynamicOperationalData] = useState(operationalData);

  useEffect(() => {
    if (operationalData && !Object.keys(operationalData).length && !Object.keys(dynamicOperationalData).length) {
      return;
    }
    if (!isEqual(dynamicOperationalData, operationalData)) {
      setDynamicOperationalData(operationalData);
    }
  }, [operationalData]);

  useEffect(() => {
    const handleWebSocketMessage = (message: any) => {
      if (message.name !== "UPDATE_BACNET_UNIT_DATA" || message.data?.unitId !== id) return;
      const newOperationalData = message.data?.operationalData;
      if (isEmpty(newOperationalData)) return;
      
      // BACnet pushes only the points that changed, so merge over what we already have
      setDynamicOperationalData((prev: any) => {
        const merged = { ...prev, ...newOperationalData };
        return isEqual(prev, merged) ? prev : merged;
      });
    };

    addWebSocketListener(handleWebSocketMessage);
    return () => removeWebSocketListener(handleWebSocketMessage);
  }, [id]);

  // ── Mapped points + readiness ─────────────────────────────────────────────

  const universalMappedPoints = useMemo(
    () => getUniversalMappedPoints(paramTemplate?.data || entry?.paramTemplateData),
    [paramTemplate?.data, entry?.paramTemplateData]
  );

  const isUniversalUnit =
    (paramTemplate?.entityTemplate ??
      entry?.entityTemplate ??
      entry?.paramTemplateData?.entityTemplate ??
      false) === true;
  const hasUniversalControls = Object.keys(universalMappedPoints).length > 0;
  const isReady = !isLoadingTemplates && isUniversalUnit && hasUniversalControls;

  // ── Key derivation ────────────────────────────────────────────────────────

  const onOffKey = universalMappedPoints["operationStatus"] ? "operationStatus" : null;
  const modeKey    = universalMappedPoints["operationMode"]    ? "operationMode"   : null;
  const setpointKey = universalMappedPoints["setpoint"]       ? "setpoint"        : null;
  const roomTempKey = universalMappedPoints["roomTemperature"] ? "roomTemperature" : null;

  // ── Current values ────────────────────────────────────────────────────────

  const currentOnOff    = onOffKey    ? getOperationalValue(onOffKey,    universalMappedPoints, dynamicOperationalData) : null;
  const currentSetpoint = setpointKey ? getOperationalValue(setpointKey, universalMappedPoints, dynamicOperationalData) : null;
  const currentRoomTemp = roomTempKey ? getOperationalValue(roomTempKey, universalMappedPoints, dynamicOperationalData) : null;

  const currentModeRaw = modeKey ? getOperationalValue(modeKey, universalMappedPoints, dynamicOperationalData) : null;
  const modeConfig     = modeKey ? universalMappedPoints[modeKey] : null;
  const currentMode    = currentModeRaw !== null && modeConfig?.customEnum
    ? formatValue(currentModeRaw, modeConfig, operationModesExtended)
    : currentModeRaw;

  // ── Mode text resolution ──────────────────────────────────────────────────

  let currentModeIndex = currentModeRaw;
  let modeText = "";

  if (typeof currentMode === "string") {
    const foundIndex = operationModesExtended?.findIndex?.(
      (m: string) => m?.toUpperCase() === currentMode.toUpperCase()
    );
    if (foundIndex !== undefined && foundIndex !== -1) {
      currentModeIndex = foundIndex;
      modeText = operationModesExtended[foundIndex];
    } else {
      modeText = currentMode.toUpperCase();
    }
  } else if (typeof currentModeIndex === "number") {
    modeText = operationModesExtended?.[currentModeIndex] || "";
  }

  // ── Derived display flags ─────────────────────────────────────────────────

  const isOn     = currentOnOff === 1 || currentOnOff === "1" || currentOnOff === true;
  const isHeatOn = isOn && modeText === "HEAT";
  const isCoolOn = isOn && modeText === "COOL";
  const isFanOn  = isOn && modeText === "FAN";

  const isStatusHeat = isHeatOn;
  const isStatusCool = isCoolOn;
  const showSetpoint = isFanOn ? false : isHeatOn || isCoolOn || !!currentSetpoint;

  // ── Temperature unit ──────────────────────────────────────────────────────

  const dataPoint = universalMappedPoints["roomTemperature"] || universalMappedPoints["setpoint"];
  const symbol    = dataPoint
    ? ((bacnetUnits as any)[dataPoint?.config?.userUnits || dataPoint?.config?.units]?.symbol || "")
    : "";
  const tempUnit  = symbol || (types.temperatureScale[temperatureScale as number] === "celsius" ? "°C" : "°F");

  // ── Handlers ──────────────────────────────────────────────────────────────

  const setBacnetObjectValueMutation = useSetBacnetObjectValue() as any;
  const setBacnetObjectValue = setBacnetObjectValueMutation.mutate;

  const handleBacnetValueChange = useCallback((objectId: string, value: any) => {
    setBacnetObjectValue(
      { unitId: id, payload: { object: objectId, value } },
      { onError: (error: any) => addErrorMessage({ message: error?.message || "Failed to update value" }) }
    );
  }, [id, setBacnetObjectValue, addErrorMessage]);

  const handleOnOffToggle = useCallback((e: any) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onOffKey) return;
    const point = universalMappedPoints[onOffKey];
    if (!point?.objectId) return;
    handleBacnetValueChange(point.objectId, isOn ? 0 : 1);
  }, [isOn, onOffKey, universalMappedPoints, handleBacnetValueChange]);

  // ── Public surface ────────────────────────────────────────────────────────

  return {
    // readiness
    isReady,
    isUniversalUnit,
    hasUniversalControls,
    isLoading: isLoadingTemplates,
    paramTemplate,
    // state
    isOn,
    isStatusHeat,
    isStatusCool,
    showSetpoint,
    modeText,
    currentMode,
    currentSetpoint,
    currentRoomTemp,
    tempUnit,
    onOffKey,
    setpointKey,
    roomTempKey,
    isFanOn,
    // handlers
    handleOnOffToggle,
  };
}